import { Controller } from "@hotwired/stimulus"

// Self-contained flash/toast lifecycle. No Bootstrap dependency — connects on
// every layout via the `application` pack's `import "controllers"`, which is
// why this replaces the old `data-bs-dismiss="alert"` close button (Bootstrap's
// Alert JS is not loaded on the auth / app-shell layouts).
//
// - dismiss(): fade + slide out, then remove (with a setTimeout fallback so it
//   still removes when transitionend doesn't fire).
// - auto-dismiss when timeoutValue > 0; animate the progress bar 100% -> 0.
// - pause()/resume() on hover/focus so a toast stays readable while pointed at.
//
// REDUCED MOTION. This controller writes its own animation from JS — an inline
// `transition` on the toast and a rAF-driven width on the progress bar — so no
// stylesheet media query can reach it. That is precisely the motion a
// prefers-reduced-motion user cannot opt out of. Under reduce nothing animates:
// the toast is removed outright and the bar is never driven. The TIMING is
// identical in both arms — auto-dismiss is not motion, and making toasts
// permanent for these users would be a different bug.
// (The old comment claimed the setTimeout fallback covered reduced motion. It
// did not: the transition still ran, and so did the progress bar.)
export default class extends Controller {
  static values = { timeout: { type: Number, default: 0 } }
  static targets = ["progress"]

  connect() {
    this.remaining = this.timeoutValue
    if (this.remaining > 0) this.startTimer()
  }

  disconnect() {
    if (this.timer) clearTimeout(this.timer)
  }

  get prefersReducedMotion() {
    return (
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    )
  }

  startTimer() {
    this.startedAt = performance.now()
    this.timer = setTimeout(() => this.dismiss(), this.remaining)
    if (this.hasProgressTarget && !this.prefersReducedMotion) {
      this.progressTarget.style.transition = `width ${this.remaining}ms linear`
      requestAnimationFrame(() => { this.progressTarget.style.width = "0%" })
    }
  }

  pause() {
    if (!this.timer) return
    clearTimeout(this.timer)
    this.timer = null
    this.remaining -= performance.now() - this.startedAt
    // Nothing was moving, so there is nothing to freeze — and writing an inline
    // width here is the one visual change reduce was meant to avoid.
    if (this.hasProgressTarget && !this.prefersReducedMotion) {
      const frozen = getComputedStyle(this.progressTarget).width
      this.progressTarget.style.transition = "none"
      this.progressTarget.style.width = frozen
    }
  }

  resume() {
    if (this.timeoutValue <= 0 || this.timer || this.remaining <= 0) return
    this.startTimer()
  }

  dismiss() {
    if (this.timer) { clearTimeout(this.timer); this.timer = null }
    const el = this.element
    const remove = () => { if (el.isConnected) el.remove() }
    if (this.prefersReducedMotion) {
      remove()
      return
    }
    el.style.transition = "opacity 0.3s ease, transform 0.3s ease"
    el.style.opacity = "0"
    el.style.transform = "translateY(8px)"
    el.addEventListener("transitionend", remove, { once: true })
    setTimeout(remove, 360)
  }
}
